import React from "react";
import {
  Box,
  Stack,
  ThemeIcon,
  Text,
  Paper,
  Button,
  Group as MantineGroup,
} from "@mantine/core";
import { isMobile as isMobileDevice } from "react-device-detect";
import {
  IconBolt,
  IconCheck,
  IconClock,
  IconLock,
  IconMessageCode,
  IconShieldCheck,
} from "@tabler/icons-react";

const DataCollectionIntro = ({
  icon,
  title,
  subtitle,
  componentKey,
  inputSize,
  data,
  onStart,
  bounceTransition,
  theme,
  skipableText,
  onSkip,
  postPayloadMessage,
}) => {
  const isCoppaIntro = componentKey === "idmatch/coppa";

  const defaultSubtitle =
    isCoppaIntro
      ? "To continue, we'll quickly confirm your identity. You'll be asked to provide your legal name, date of birth, and SSN."
      : componentKey === "phonematch"
      ? "Provide your name and phone number so we can verify them securely before sending your code."
      : "To continue, we'll quickly confirm your identity. You'll be asked to provide your legal name, address, and date of birth.";

  const introSubtitle = subtitle || defaultSubtitle;

  const features =
    componentKey === "phonematch"
      ? [
          { Icon: IconShieldCheck, text: "Verified through trusted and secure data sources" },
          { Icon: IconLock, text: "Your data is protected and never shared" },
          { Icon: IconClock, text: "Complete verification in under a minute" },
        ]
      : [
          { Icon: IconCheck, text: "Simple and secure identity check" },
          {
            Icon: IconLock,
            text: "Your details are kept private and protected",
          },
          { Icon: IconClock, text: "Takes less than 1 minute to complete" },
        ];

  const primaryColorKey =
    data.styles?.primaryColor || theme?.primaryColor || "teal";

  const resolvePrimaryShade = () => {
    if (!theme) {
      return 5;
    }

    if (typeof theme.primaryShade === "number") {
      return theme.primaryShade;
    }

    if (theme.colorScheme === "dark") {
      return theme.primaryShade?.dark ?? 5;
    }

    return theme.primaryShade?.light ?? 5;
  };

  const primaryShade = resolvePrimaryShade();
  const themeColorArray = theme?.colors?.[primaryColorKey];
  const iconColor = themeColorArray
    ? themeColorArray[primaryShade] ?? primaryColorKey
    : primaryColorKey;

  const themedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon, {
        color: iconColor,
      })
    : icon;

  return (
    <Box
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        paddingTop: isCoppaIntro ? (inputSize === "md" ? 20 : 16) : inputSize === "md" ? 32 : 24,
      }}
    >
      <Box>
        <Stack
          spacing={isCoppaIntro ? (inputSize === "md" ? "sm" : "xs") : inputSize === "md" ? "md" : "sm"}
          align="center"
          style={{
            flexGrow: 1,
            width: "100%",
            maxWidth: isCoppaIntro ? (inputSize === "md" ? 460 : 360) : undefined,
            margin: "0 auto",
          }}
        >
          <ThemeIcon
            variant="light"
            color={primaryColorKey}
            size={isCoppaIntro ? (inputSize === "md" ? 60 : 50) : inputSize === "md" ? 85 : 70}
            radius="xl"
            style={{
              color: iconColor,
            }}
          >
            {themedIcon}
          </ThemeIcon>
          <Text
            fz={isCoppaIntro ? (inputSize === "md" ? 26 : 22) : inputSize === "md" ? 30 : 24}
            fw={700}
            align="center"
          >
            {title}
          </Text>
          <Text
            align="center"
            fz={isCoppaIntro ? (inputSize === "md" ? "lg" : "md") : inputSize}
            style={{ maxWidth: isCoppaIntro ? (inputSize === "md" ? 420 : 340) : undefined }}
          >
            {introSubtitle}
          </Text>
          <Paper
            withBorder
            p={isCoppaIntro ? (inputSize === "md" ? "sm" : "xs") : inputSize === "md" ? "md" : "sm"}
            style={{
              backgroundColor:
                theme.colorScheme === "dark" ? theme.colors.dark[6] : "#fafafa",
              width: "100%",
              maxWidth: isCoppaIntro ? (inputSize === "md" ? 460 : 360) : undefined,
              margin: "0 auto",
            }}
          >
            <Stack spacing={isCoppaIntro ? (inputSize === "md" ? "md" : "sm") : inputSize === "md" ? "lg" : "md"}>
              {features.map(({ Icon: FeatureIcon, text: featureText }) => (
                <MantineGroup key={featureText} noWrap spacing="sm">
                  <FeatureIcon size={isCoppaIntro ? (inputSize === "md" ? 24 : 20) : inputSize === "md" ? 30 : 24} />
                  <Text fz={isCoppaIntro ? (inputSize === "md" ? 16 : 15) : inputSize === "md" ? 18 : 16}>
                    {featureText}
                  </Text>
                </MantineGroup>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Box>
      <Box
        style={{
          marginTop: isCoppaIntro ? "12px" : "15px",
          marginBottom: isMobileDevice
            ? isCoppaIntro
              ? "20px"
              : "32px"
            : isCoppaIntro
            ? "16px"
            : "20px",
        }}
      >
        <Stack spacing="sm">
          <Button
            size="lg"
            fullWidth
            onClick={() => {
              // Send postMessage for get started click
              if (postPayloadMessage) {
                postPayloadMessage({
                  event: "GET_STARTED_CLICKED",
                  app: componentKey,
                });
              }
              onStart();
            }}
            sx={{
              transition: `all 0.5s ${bounceTransition}`,
              "&:hover": { transform: "scale(1.01)" },
            }}
          >
            Get started
          </Button>
          {skipableText && onSkip && (
            <Button
              variant="subtle"
              size="md"
              fullWidth
              onClick={onSkip}
              sx={{
                color: theme.colorScheme === "dark" ? theme.colors.blue[4] : theme.colors.blue[6],
                textDecoration: "underline",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[0],
                  textDecoration: "underline",
                  color: theme.colorScheme === "dark" ? theme.colors.blue[3] : theme.colors.blue[7],
                },
              }}
            >
              {skipableText}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default DataCollectionIntro;
